import "mdast-util-math";
import React, { useState, useMemo } from "react";
import remarkMath from "remark-math";
let katex;
let loadingKatex;
let loadingKatexStyle;
function getKatex(useInnerCss) {
  return new Promise((resolve, reject) => {
    if (katex) {
      resolve(katex);
    } else {
      if (typeof useInnerCss === "function") {
        loadingKatexStyle != null ? loadingKatexStyle : loadingKatexStyle = useInnerCss();
      } else if (useInnerCss) {
        loadingKatexStyle != null ? loadingKatexStyle : loadingKatexStyle = import("./katex.css.e44a6e5b.mjs");
      }
      loadingKatex != null ? loadingKatex : loadingKatex = import("katex").then((m) => katex = m.default);
      loadingKatex.then(resolve, reject);
    }
  });
}
function hasLoadedKatex() {
  return katex;
}
const createMathComponent = (Component, { displayMode }) => {
  const MathComponent = ({
    math,
    katexOptions,
    renderError,
    useInnerCss,
    ...rest
  }) => {
    const [KaTeX, setKaTeX] = useState(() => {
      if (!hasLoadedKatex()) {
        getKatex(useInnerCss).then((m) => {
          setKaTeX((prev) => {
            if (prev === m)
              return prev;
            return m;
          });
        });
      }
      return hasLoadedKatex();
    });
    const formula = math;
    const options = useMemo(() => {
      return {
        displayMode,
        ...katexOptions,
        throwOnError: renderError ? true : katexOptions == null ? void 0 : katexOptions.throwOnError
      };
    }, [katexOptions]);
    const { html, error } = useMemo(() => {
      if (!KaTeX)
        return { html: void 0, error: void 0 };
      try {
        const html2 = KaTeX.renderToString(formula, options);
        return { html: html2, error: void 0 };
      } catch (error2) {
        if (error2 instanceof KaTeX.ParseError || error2 instanceof TypeError) {
          return { error: error2 };
        }
        throw error2;
      }
    }, [KaTeX, formula, options, renderError]);
    if (error) {
      return renderError ? /* @__PURE__ */ React.createElement(React.Fragment, null, renderError(error)) : /* @__PURE__ */ React.createElement(Component, { html: `${error.message}`, ...rest });
    }
    if (html !== void 0) {
      return /* @__PURE__ */ React.createElement(Component, { html, ...rest });
    }
    return /* @__PURE__ */ React.createElement(React.Fragment, null);
  };
  return MathComponent;
};
const InternalInlineMath = ({
  html,
  ...rest
}) => {
  return /* @__PURE__ */ React.createElement(
    "span",
    {
      "data-testid": "react-katex",
      dangerouslySetInnerHTML: { __html: html },
      ...rest
    }
  );
};
const InlineMath = createMathComponent(InternalInlineMath, {
  displayMode: false
});
const InternalBlockMath = ({
  html,
  ...rest
}) => {
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      "data-testid": "react-katex",
      dangerouslySetInnerHTML: { __html: html },
      ...rest
    }
  );
};
const BlockMath = createMathComponent(InternalBlockMath, {
  displayMode: true
});
function pluginViewerMath({
  katexOptions,
  useInnerCss,
  renderError
} = {}) {
  return {
    remark: (processor) => processor.use(remarkMath),
    mdxRemark: (processor) => processor.use(remarkMath),
    viewerEffect({ markdownBody }) {
      const renderMath = async (selector, displayMode) => {
        const els = markdownBody.querySelectorAll(selector);
        if (els.length === 0)
          return;
        getKatex(useInnerCss).then((katex2) => {
          els.forEach((el) => {
            katex2.render(el.innerText, el, {
              ...katexOptions,
              throwOnError: false,
              displayMode
            });
          });
        });
      };
      renderMath(".math.math-inline", false);
      renderMath(".math.math-display", true);
    },
    mdxHandlerList: ({ ctx }) => (input) => {
      if ("res" in input)
        return input;
      const node = input.source;
      if (node.type === "inlineMath") {
        return {
          res: /* @__PURE__ */ React.createElement(
            InlineMath,
            {
              math: node.value,
              katexOptions,
              useInnerCss,
              renderError,
              ...ctx.calcRequireIndexProps(input)
            }
          )
        };
      }
      if (node.type === "math") {
        return {
          res: /* @__PURE__ */ React.createElement(
            BlockMath,
            {
              math: node.value,
              katexOptions,
              useInnerCss,
              renderError,
              ...ctx.calcRequireIndexProps(input)
            }
          )
        };
      }
      return input;
    }
  };
}
const block = "Block formula";
const blockText = "formula";
const inline = "Inline formula";
const inlineText = "formula";
const en = {
  block,
  blockText,
  inline,
  inlineText
};
function getMathActions(locale) {
  return [
    {
      icon: `
        <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="none" viewBox="0 0 48 48">
          <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="m40 9-3-3H8l18 18L8 42h29l3-3"></path>
        </svg>
      `,
      handler: {
        type: "dropdown",
        actions: [
          {
            title: locale.inline,
            icon: `
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="none" viewBox="0 0 48 48">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="m37 9-3-3H8l17 18L8 42h26l3-3M5 24h10M33 24h10"></path>
              </svg>
            `,
            cheatsheet: `$${locale.inlineText}$`,
            handler: {
              type: "action",
              click({ wrapText, editor }) {
                wrapText("$");
                editor.focus();
              }
            }
          },
          {
            title: locale.block,
            icon: `
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="none" viewBox="0 0 48 48">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="m36 11-3-3H12l16 16-16 16h21l3-3M6 5v38M42 5v38"></path>
              </svg>
            `,
            cheatsheet: `$$\u21B5${locale.blockText}\u21B5$$`,
            handler: {
              type: "action",
              click({ appendBlock, editor, codemirror }) {
                const { line } = appendBlock("$$\n\\TeX\n$$");
                editor.setSelection(
                  codemirror.Pos(line + 1, 0),
                  codemirror.Pos(line + 1, 4)
                );
                editor.focus();
              }
            }
          }
        ]
      }
    }
  ];
}
function pluginEditorMath({
  locale: _locale
} = {}) {
  const locale = { ...en, ..._locale };
  return {
    actions: getMathActions(locale)
  };
}
export {
  pluginEditorMath,
  pluginViewerMath
};
